import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';

const heroMovies = movies.filter((m) => m.isHot && m.category === 'dianying').slice(0, 4);

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrent(index);
      setTimeout(() => setIsTransitioning(false), 600);
    },
    [isTransitioning]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % heroMovies.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const movie = heroMovies[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      <div className="absolute inset-0 bg-background-100">
        <img
          src={movie.stills[0]}
          alt={movie.title}
          className={`w-full h-full object-cover object-top transition-opacity duration-700 ${isTransitioning ? 'opacity-30' : 'opacity-100'}`}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background-50"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent"></div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-8 md:pb-14">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row items-end md:items-center gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              {movie.isVip && (
                <span className="px-2.5 py-0.5 bg-accent-500 text-background-950 text-xs font-bold rounded-md">
                  VIP
                </span>
              )}
              <span className="text-sm text-white/70">{movie.year}</span>
              <span className="text-sm text-white/70">{movie.duration}</span>
              <span className="text-sm text-white/70">{movie.region}</span>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-3 tracking-wide">
              {movie.title}
            </h2>
            <p className="text-sm md:text-base text-white/70 max-w-xl line-clamp-2 mb-4">
              {movie.description}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              {movie.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-white/10 backdrop-blur-sm text-white/80 text-xs rounded-full border border-white/10"
                >
                  {tag}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-3 mt-5">
              <Link
                to={`/article/${movie.id}`}
                className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-play-fill text-lg"></i>
                立即观看
              </Link>
              <Link
                to={`/article/${movie.id}`}
                className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-2.5 bg-white/10 backdrop-blur-sm text-white text-sm font-semibold rounded-full hover:bg-white/20 transition-colors cursor-pointer border border-white/20"
              >
                <i className="ri-information-line text-lg"></i>
                详细信息
              </Link>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3 shrink-0">
            {heroMovies.map((m, i) => (
              <button
                key={m.id}
                onClick={() => goTo(i)}
                className={`w-[100px] h-[140px] rounded-lg overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                  i === current
                    ? 'border-primary-500 scale-105 shadow-lg'
                    : 'border-white/20 opacity-60 hover:opacity-90 hover:border-white/40'
                }`}
              >
                <img src={m.poster} alt={m.title} className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroMovies.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              i === current ? 'bg-primary-500 w-6' : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`切换到第 ${i + 1} 张`}
          />
        ))}
      </div>
    </section>
  );
}